'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';
import MfaVerify from '@/components/MfaVerify';
import MfaEnroll from '@/components/MfaEnroll';

type LoginStep = 'credentials' | 'mfa-verify' | 'mfa-enroll';

export default function LoginPage() {
  const router = useRouter();
  // Use supabase client directly — do NOT use AuthContext.signIn here.
  // AuthContext.onAuthStateChange fires immediately on signInWithPassword which
  // sets user state and can cause a re-render that loses the MFA step state.
  // By calling supabase directly we control exactly when the session is committed.
  const supabase = createClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState<LoginStep>('credentials');
  const [mfaFactorId, setMfaFactorId] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [pendingToken, setPendingToken] = useState('');

  const logLoginAttempt = async (
    userId: string | null,
    success: boolean,
    failureReason?: string,
    status?: string
  ) => {
    try {
      await fetch('/api/security/log-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email,
          success,
          failureReason: failureReason ?? null,
          portalLogin: false,
          userAgent: navigator.userAgent,
          status: status ?? (success ? 'ok' : 'failed'),
        }),
      });
    } catch {
      // Non-critical
    }
  };

  const recordRateLimitAttempt = async (success: boolean) => {
    try {
      await fetch('/api/security/rate-limit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ success }),
      });
    } catch {
      // Non-critical
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // ── Step 1: Check region access ──────────────────────────────────────
      const accessRes = await fetch(
        `/api/security/check-access?email=${encodeURIComponent(email)}`
      );
      let accessData: { allowed: boolean; reason?: string } = { allowed: true };
      try {
        const contentType = accessRes.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          accessData = await accessRes.json();
        }
      } catch {
        // If geo check fails to parse, fail open (allow login to proceed)
      }
      if (!accessData.allowed) {
        setError('Access denied: your region is not permitted to access this platform.');
        setLoading(false);
        return;
      }

      // ── Step 2: Check rate limit / lockout ───────────────────────────────
      const rateLimitRes = await fetch('/api/security/rate-limit');
      let rateLimitData: { allowed: boolean; reason?: string; retryAfterSeconds?: number } = { allowed: true };
      try {
        const contentType = rateLimitRes.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          rateLimitData = await rateLimitRes.json();
        }
      } catch {
        // If rate limit check fails to parse, fail open
      }
      if (!rateLimitData.allowed) {
        const reason = rateLimitData.reason === 'locked_out'
          ? `Your IP has been temporarily locked out due to repeated failed login attempts. Please try again in ${Math.ceil((rateLimitData.retryAfterSeconds ?? 900) / 60)} minutes.`
          : `Too many login attempts. Please wait ${rateLimitData.retryAfterSeconds ?? 300} seconds before trying again.`;
        await logLoginAttempt(null, false, reason, 'rate_limited');
        setError(reason);
        setLoading(false);
        return;
      }

      // ── Step 3: Sign in directly via Supabase (bypass AuthContext) ───────
      // We call supabase directly so that onAuthStateChange in AuthContext does
      // NOT fire yet. This keeps the login page in control of the MFA flow.
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        await recordRateLimitAttempt(false);
        await logLoginAttempt(null, false, signInError.message, 'failed');
        setError(signInError.message || 'Invalid credentials. Please try again.');
        setLoading(false);
        return;
      }

      const userId = data?.user?.id ?? null;
      const token = data?.session?.access_token ?? null;
      const refreshToken = data?.session?.refresh_token ?? null;

      // Successful auth — reset rate limit counter
      await recordRateLimitAttempt(true);

      // ── Step 3b: Persist session server-side via Set-Cookie headers ───────
      // Call the set-session API route so the server writes the session as
      // proper Set-Cookie response headers. This guarantees the middleware can
      // read the session on the very next request, regardless of whether
      // document.cookie is reliably sent in the iframe preview environment.
      if (token && refreshToken) {
        try {
          await fetch('/api/auth/set-session', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token: token, refresh_token: refreshToken }),
          });
          // Small delay to allow the browser to commit the Set-Cookie headers
          // from the response before the router navigates to the next page.
          await new Promise((resolve) => setTimeout(resolve, 150));
        } catch {
          // Non-critical — fall back to client-side cookie
        }
      }

      // ── Step 4: Check MFA requirement ────────────────────────────────────
      if (token) {
        const mfaRes = await fetch('/api/security/mfa-verify', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mfaData = await mfaRes.json();

        if (mfaData.mfaRequired) {
          setPendingUserId(userId ?? '');
          setPendingToken(token);

          if (mfaData.totpEnrolled && mfaData.factorId) {
            setMfaFactorId(mfaData.factorId);
            setStep('mfa-verify');
          } else {
            setStep('mfa-enroll');
          }
          setLoading(false);
          return;
        }
      }

      // No MFA required — log success and redirect
      await logLoginAttempt(userId, true, undefined, 'ok');

      // Check if user is dual-role — if so, send to choice screen
      if (userId) {
        const supabaseClient = createClient();
        const { data: profile } = await supabaseClient
          .from('user_profiles')
          .select('is_dual_role')
          .eq('id', userId)
          .single();
        if (profile?.is_dual_role) {
          router.push('/role-select');
          return;
        }
      }

      router.push('/dashboard');
    } catch (err: any) {
      await recordRateLimitAttempt(false);
      await logLoginAttempt(null, false, err?.message, 'failed');
      setError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = async () => {
    await logLoginAttempt(pendingUserId || null, true, undefined, 'ok');

    // Check if user is dual-role
    if (pendingUserId) {
      const supabaseClient = createClient();
      const { data: profile } = await supabaseClient
        .from('user_profiles')
        .select('is_dual_role')
        .eq('id', pendingUserId)
        .single();
      if (profile?.is_dual_role) {
        router.push('/role-select');
        return;
      }
    }

    router.push('/dashboard');
  };

  const handleMfaEnrollComplete = async () => {
    await logLoginAttempt(pendingUserId || null, true, undefined, 'ok');

    // Check if user is dual-role
    if (pendingUserId) {
      const supabaseClient = createClient();
      const { data: profile } = await supabaseClient
        .from('user_profiles')
        .select('is_dual_role')
        .eq('id', pendingUserId)
        .single();
      if (profile?.is_dual_role) {
        router.push('/role-select');
        return;
      }
    }

    router.push('/dashboard');
  };

  const handleMfaCancel = async () => {
    await supabase.auth.signOut();
    setStep('credentials');
    setEmail('');
    setPassword('');
    setMfaFactorId('');
    setPendingUserId('');
    setPendingToken('');
  };

  return (
    <main className="min-h-screen bg-navy flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue/3 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Welcome heading */}
        <div className="flex flex-col items-center mb-8 gap-3">
          <Image
            src="/assets/images/PretiumLogo-1779974146378.png"
            alt="Pretium+ Logo"
            width={160}
            height={60}
            className="object-contain"
            priority
          />
          <h1 className="font-display text-3xl font-bold text-white mt-2 text-center">Welcome to Pretium+</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-yellow-alert border border-yellow-alert/40 bg-yellow-alert/10 px-2.5 py-1 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-alert pulse-dot" />
              Internal Use Only
            </span>
          </div>
          <p className="text-silver text-sm text-center mt-1">Motor Insurance MGA Platform</p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          {step === 'credentials' && (
            <>
              <h2 className="font-display text-2xl font-bold text-white mb-1">Sign in</h2>
              <p className="text-silver text-sm mb-6">Enter your credentials to access the platform.</p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-silver">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@pretiumagency.co.uk"
                    autoComplete="email"
                    className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all duration-200"
                    required
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-wider text-silver">Password</label>
                    <Link href="/forgot-password" className="text-xs text-blue hover:text-blue/80 transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 pr-11 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all duration-200"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-silver hover:text-white transition-colors duration-200"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-red-alert/10 border border-red-alert/30">
                    <svg className="w-4 h-4 text-red-alert flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className="text-red-alert text-xs leading-relaxed">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-electric w-full py-3 rounded-lg text-sm font-semibold tracking-wide uppercase mt-1 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in…
                    </>
                  ) : (
                    'Sign in to Pretium+'
                  )}
                </button>
              </form>
            </>
          )}

          {step === 'mfa-verify' && mfaFactorId && (
            <MfaVerify
              factorId={mfaFactorId}
              accessToken={pendingToken}
              onSuccess={handleMfaSuccess}
              onCancel={handleMfaCancel}
            />
          )}

          {step === 'mfa-enroll' && (
            <MfaEnroll
              onComplete={handleMfaEnrollComplete}
            />
          )}
        </div>

        <p className="text-center text-xs text-silver-dim mt-6">
          © {new Date().getFullYear()} Pretium+ — Confidential Internal Platform
        </p>
      </div>
    </main>
  );
}
